/**
 * Getting into a vehicle and out again, as a few seconds of animation.
 *
 * Getting into a car: walk up to the driver's door, open it, turn and sit on
 * the edge of the seat with the feet still outside, swing the legs in and
 * take the wheel, pull the door shut. Getting out is the same run backwards.
 * Getting on a motorbike is a walk up and a leg over; onto a scooter, a step
 * up onto the deck, one foot and then the other.
 *
 * The body is posed in the vehicle's own frame through the crowd's rider
 * rig (`CharacterCrowd.placeRider`). Hands and feet are targets, and the arms
 * and legs reach for them. So a pose here is a handful of points, blended
 * between a few keyframes.
 */

import { Vector3 } from 'three';
import type { RiderPose } from './crowd.js';
import { BODY } from './rig.js';

/** Where the driver sits and reaches in a car, as `VehicleModel.seat` gives it. */
export interface Seat {
  hipY: number;
  hipZ: number;
  wheelY: number;
  wheelZ: number;
  pedalY: number;
  pedalZ: number;
  /** The seat's x, and where to stand outside the door. */
  x: number;
  outsideX: number;
}

function smooth(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

const v = (x: number, y: number, z: number) => new Vector3(x, y, z);

function blank(): RiderPose {
  return {
    hips: { x: 0, y: 0, z: 0, pitch: 0, roll: 0, yaw: 0 },
    torso: { pitch: 0, roll: 0, twist: 0 },
    head: { pitch: 0, yaw: 0 },
    hands: [v(0, 0, 0), v(0, 0, 0)], elbows: [v(0, 0, 0), v(0, 0, 0)],
    feet: [v(0, 0, 0), v(0, 0, 0)], knees: [v(0, 0, 0), v(0, 0, 0)],
    toes: [0, 0],
    helmet: false,
  };
}

/** Blend two poses into `out`: `t` 0 is `a`, 1 is `b`. */
export function blendPose(a: RiderPose, b: RiderPose, t: number, out: RiderPose = blank()): RiderPose {
  const m = (x: number, y: number) => x + (y - x) * t;
  for (const k of ['x', 'y', 'z', 'pitch', 'roll', 'yaw'] as const) out.hips[k] = m(a.hips[k], b.hips[k]);
  for (const k of ['pitch', 'roll', 'twist'] as const) out.torso[k] = m(a.torso[k], b.torso[k]);
  out.head.pitch = m(a.head.pitch, b.head.pitch);
  out.head.yaw = m(a.head.yaw, b.head.yaw);
  for (const i of [0, 1] as const) {
    out.hands[i].lerpVectors(a.hands[i], b.hands[i], t);
    out.elbows[i].lerpVectors(a.elbows[i], b.elbows[i], t);
    out.feet[i].lerpVectors(a.feet[i], b.feet[i], t);
    out.knees[i].lerpVectors(a.knees[i], b.knees[i], t);
    out.toes[i] = m(a.toes[i], b.toes[i]);
  }
  out.helmet = t < 0.5 ? a.helmet : b.helmet;
  return out;
}

/**
 * Standing upright at (x, z) in a vehicle's frame, turned `yaw` (positive
 * right), arms at the sides.
 */
export function standingPose(x: number, z: number, yaw: number, helmet = false): RiderPose {
  const p = blank();
  const c = Math.cos(yaw), s = Math.sin(yaw);
  // The body's right and forward, in the vehicle's frame (x right, z back).
  const rx = c, rz = s, fx = s, fz = -c;
  p.hips = { x, y: BODY.hip, z, pitch: 0, roll: 0, yaw };
  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    p.hands[i].set(x + rx * side * 0.25, BODY.hip - 0.02, z + rz * side * 0.25);
    p.elbows[i].set(-fx + rx * side * 0.3, 0, -fz + rz * side * 0.3);
    p.feet[i].set(x + rx * side * 0.11, 0.07, z + rz * side * 0.11);
    p.knees[i].set(fx, 0, fz);
  }
  p.helmet = helmet;
  return p;
}

/** Sitting on the edge of the seat, turned out toward the door, feet on the ground outside. */
function perchedPose(seat: Seat): RiderPose {
  const p = blank();
  const x = seat.x - 0.3, z = seat.hipZ;
  p.hips = { x, y: seat.hipY + 0.06, z, pitch: 0, roll: 0, yaw: -1.25 };
  p.torso.pitch = 0.1;
  const footX = seat.outsideX + 0.35;
  p.feet[0].set(footX, 0.07, z - 0.3);
  p.feet[1].set(footX + 0.05, 0.07, z + 0.02);
  for (const i of [0, 1] as const) p.knees[i].set(-1, 0.4, -0.3);
  // One hand on the door frame, one on the seat.
  p.hands[0].set(seat.x - 0.25, seat.hipY + 0.55, z - 0.62);
  p.hands[1].set(seat.x + 0.05, seat.hipY + 0.02, z + 0.1);
  p.elbows[0].set(-1, -0.2, 0);
  p.elbows[1].set(0.5, -0.2, 1);
  return p;
}

/** At the wheel: hips in the seat, hands on the rim, feet on the pedals. */
export function drivingPose(seat: Seat): RiderPose {
  const p = blank();
  p.hips = { x: seat.x, y: seat.hipY + 0.04, z: seat.hipZ, pitch: 0, roll: 0, yaw: 0 };
  p.torso.pitch = -0.08;
  p.head.pitch = 0.05;
  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    p.hands[i].set(seat.x + side * 0.16, seat.wheelY + 0.05, seat.wheelZ + 0.04);
    p.elbows[i].set(side, -1, 0.3);
    p.feet[i].set(seat.x + side * 0.14, seat.pedalY + 0.06, seat.pedalZ);
    p.knees[i].set(side * 0.2, 1, -0.6);
    p.toes[i] = 0.4;
  }
  return p;
}

/** Durations of each stage, seconds. */
export const BOARDING = {
  /** Car: door opening, sitting in (or getting out), door closing. */
  door: 0.4,
  sit: 0.9,
  /** A bike: one leg over. */
  legOver: 0.75,
  /** A scooter: a step up onto the deck. */
  stepOn: 0.7,
} as const;

/**
 * The body in a car, `t` seconds into getting in (or out, with `out`): its
 * pose and how far open the door is, 0..1. `done` once it is over.
 */
export function carBoarding(seat: Seat, t: number, out: boolean, into: RiderPose): { pose: RiderPose; door: number; done: boolean } {
  const { door, sit } = BOARDING;
  const total = door * 2 + sit;
  const time = out ? total - t : t;
  const stand = standingPose(seat.outsideX, seat.hipZ - 0.15, 0);
  const perched = perchedPose(seat);
  const driving = drivingPose(seat);
  let pose: RiderPose;
  const s = (time - door) / sit;
  if (s <= 0) pose = blendPose(stand, stand, 0, into);
  else if (s < 0.45) pose = blendPose(stand, perched, smooth(s / 0.45), into);
  else pose = blendPose(perched, driving, smooth((s - 0.45) / 0.55), into);
  const open = time < door ? smooth(time / door) : time < door + sit ? 1 : smooth(1 - (time - door - sit) / door);
  return { pose, door: open, done: t >= total };
}

/** The body swinging a leg over a bike: from standing on its left, to `ride`. */
export function bikeBoarding(ride: RiderPose, t: number, out: boolean, into: RiderPose): { pose: RiderPose; done: boolean } {
  const s = smooth((out ? BOARDING.legOver - t : t) / BOARDING.legOver);
  const stand = standingPose(-0.55, ride.hips.z - 0.1, 0, ride.helmet);
  const pose = blendPose(stand, ride, s, into);
  // The leg comes up and over the seat in the middle of it.
  const lift = Math.sin(Math.PI * s) * 0.45;
  pose.feet[1].y += lift;
  pose.feet[1].x -= Math.sin(Math.PI * s) * 0.1;
  return { pose, done: t >= BOARDING.legOver };
}

/**
 * The body stepping onto a scooter's deck from standing on its left, to
 * `ride`: the right foot up onto the deck first, the body over it, the left
 * foot up after. Out is the same backwards.
 */
export function scooterBoarding(ride: RiderPose, t: number, out: boolean, into: RiderPose): { pose: RiderPose; done: boolean } {
  const k = Math.min(1, Math.max(0, (out ? BOARDING.stepOn - t : t) / BOARDING.stepOn));
  const stand = standingPose(-0.5, ride.hips.z, 0, ride.helmet);
  const pose = blendPose(stand, ride, smooth(k), into);
  // Each foot in turn lifts clear of the deck's edge as it crosses.
  const right = Math.sin(Math.PI * Math.min(1, k / 0.55));
  const left = k < 0.4 ? 0 : Math.sin(Math.PI * Math.min(1, (k - 0.4) / 0.6));
  pose.feet[1].y += right * 0.22;
  pose.feet[0].y += left * 0.2;
  return { pose, done: t >= BOARDING.stepOn };
}

/** Where a helicopter's step is, in its frame. */
export interface Step {
  x: number;
  y: number;
  z: number;
}

/** Durations of each stage of getting into a helicopter, seconds. */
export const HELI_BOARDING = { door: 0.45, step: 0.7, hoist: 0.75, sit: 0.6 } as const;

/** Facing the door from outside, one foot up on the step, hands on the door frame. */
function stepUpPose(seat: Seat, step: Step): RiderPose {
  const p = blank();
  const yaw = 1.35;
  const x = step.x - 0.42, z = step.z + 0.05;
  p.hips = { x, y: BODY.hip + 0.22, z, pitch: 0, roll: 0, yaw };
  p.torso.pitch = 0.22;
  p.head.pitch = -0.15;
  p.feet[0].set(x - 0.12, 0.07, z - 0.16);
  p.feet[1].set(step.x - 0.02, step.y + 0.05, step.z);
  // Knees ahead, toward the airframe; the raised one up and out.
  p.knees[0].set(1, 0, -0.2);
  p.knees[1].set(1, 0.6, 0.2);
  p.hands[0].set(seat.outsideX + 0.95, seat.hipY + 0.62, seat.hipZ - 0.6);
  p.hands[1].set(seat.outsideX + 0.95, seat.hipY + 0.6, seat.hipZ + 0.55);
  p.elbows[0].set(-0.3, -1, -0.6);
  p.elbows[1].set(-0.3, -1, 0.6);
  return p;
}

/** Hoisted up onto the sill: sitting on the seat's edge, turned half in, a foot still on the step. */
function onSillPose(seat: Seat, step: Step): RiderPose {
  const p = blank();
  const x = seat.x - 0.28, z = seat.hipZ + 0.05;
  p.hips = { x, y: seat.hipY + 0.05, z, pitch: 0, roll: 0, yaw: 0.9 };
  p.torso.pitch = 0.12;
  p.feet[0].set(step.x + 0.02, step.y + 0.05, step.z);
  p.feet[1].set(seat.x + 0.05, seat.pedalY + 0.02, seat.hipZ - 0.45);
  p.knees[0].set(-0.6, 0.6, -0.4);
  p.knees[1].set(0.3, 1, -0.6);
  p.hands[0].set(seat.outsideX + 0.95, seat.hipY + 0.62, seat.hipZ - 0.6);
  p.hands[1].set(seat.x + 0.25, seat.hipY, seat.hipZ + 0.05);
  p.elbows[0].set(-0.5, -1, -0.4);
  p.elbows[1].set(0.6, -0.4, 0.8);
  return p;
}

/**
 * The body getting into a helicopter, `t` seconds in (or out, with `out`):
 * the door swings open, a foot up on the step with the hands on the frame,
 * a hoist up onto the seat's edge, the legs swung in to the pedals and the
 * hands to the controls, the door shut. Out is the same, backwards.
 */
export function heliBoarding(seat: Seat, step: Step, t: number, out: boolean, into: RiderPose): { pose: RiderPose; door: number; done: boolean } {
  const { door, step: up, hoist, sit } = HELI_BOARDING;
  const total = door * 2 + up + hoist + sit;
  const time = out ? total - t : t;
  const stand = standingPose(seat.outsideX, step.z + 0.05, 1.35);
  const stepped = stepUpPose(seat, step);
  const sill = onSillPose(seat, step);
  const flying = drivingPose(seat);
  const s = time - door;
  let pose: RiderPose;
  if (s <= 0) pose = blendPose(stand, stand, 0, into);
  else if (s < up) pose = blendPose(stand, stepped, smooth(s / up), into);
  else if (s < up + hoist) {
    const k = (s - up) / hoist;
    pose = blendPose(stepped, sill, smooth(k), into);
    // Up and over the sill: the hips rise a little higher than they end.
    pose.hips.y += Math.sin(Math.PI * k) * 0.12;
  } else pose = blendPose(sill, flying, smooth((s - up - hoist) / sit), into);
  const inside = door + up + hoist + sit;
  const open = time < door ? smooth(time / door) : time < inside ? 1 : smooth(1 - (time - inside) / door);
  return { pose, door: open, done: t >= total };
}
